import { forwardRef, useCallback, useContext, useMemo } from 'react';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Popper from '@mui/material/Popper';
import Color from 'color';
import PopupState, { bindToggle, bindPopper } from 'material-ui-popup-state';
import { MoreHorizontal } from 'react-feather';
import type { ProjectConferenceTagWithMetadata, ProjectConferenceTag } from '@trinity/chat/api/interfaces/projects';
import { useModal } from '@trinity/chat-components/components/Modal/hooks/index.js';
import { PopperMenu, PopperMenuItem } from '@trinity/chat-components/components/Popper/index.js';
import { Tooltip } from '@trinity/chat-components/components/presentation/Tooltip.js';
import { ConferenceTagType } from '@/enums/conference';
import { cx } from '@trinity/chat-components';
import styles from './Column/Column.module.css';
import { TagStateContext, UpdateTagContext } from './Context';
import { Tags } from './interfaces';
import * as Modal from './Modal';
import { useDeleteTagModal } from './Modal.DeleteTag';

const editableTagTypes = [ConferenceTagType.Project, ConferenceTagType.UserCreated];

const isTagEditable = (tag: ProjectConferenceTag) => {
  return editableTagTypes.includes(tag.base.typeId);
};

type Props = {
  hasChildren: boolean;
  tag: ProjectConferenceTagWithMetadata;
};

export const Cell = ({ hasChildren, tag, ...props }: Props) => {
  const { state, projectId } = useContext(TagStateContext);

  const canEdit = isTagEditable(tag);

  const [openEditModal, EditModal] = useModal(Modal.EditTag, { keepMounted: false });
  const [openCreateModal, CreateModal] = useModal(Modal.CreateTag, { keepMounted: false });
  const [toggleDeleteAlert, DeleteModal] = useDeleteTagModal({
    projectId,
    tagId: tag.id,
  });

  const defaultColor = useMemo(() => new Color(tag.base.color).lighten(Tags.LIGHTEN_FACTOR).hex(), [tag.base.color]);

  const canDelete = useMemo(() => {
    return !hasChildren;
  }, [hasChildren]);

  return (
    <div className={`${styles.column} ${styles.summaryColumn}`} role="cell">
      <PopupState
        popupId="project-tags-actions-popper"
        variant="popper">
        {popup =>
          <div>
            <div {...bindToggle(popup)}>
              <div style={!tag.hidden ? { cursor: 'pointer' } : {}}>
                <MoreHorizontal className={cx(styles.more, { [styles.open]: popup.isOpen })} />
              </div>
            </div>
            <Popper
              {...bindPopper(popup)}
              placement="bottom">
              <ClickAwayListener onClickAway={() => popup.close()}>
                <MenuItems
                  close={popup.close}
                  tag={tag}
                  onEditTag={openEditModal}
                  onCreateTag={openCreateModal}
                  onDeleteTag={canDelete ? toggleDeleteAlert : undefined}
                />
              </ClickAwayListener>
            </Popper>
          </div>}
      </PopupState>
      <EditModal
        tag={tag}
        hasChildren={hasChildren} />
      <CreateModal
        parentTagId={tag.id}
        defaultColor={defaultColor} />
      <DeleteModal />
    </div>
  );
};

type MenuItemProps = {
  close?: () => void;
  onEditTag: () => void;
  onCreateTag: () => void;
  onDeleteTag: () => void;
  tag: ProjectConferenceTagWithMetadata;
};

const MenuItems = forwardRef(({ close, tag, onCreateTag, onDeleteTag }: MenuItemProps, ref: React.Ref<HTMLDivElement>) => {
  const mutation = useContext(UpdateTagContext);

  const closeWrapper = useCallback((fn: () => void) => {
    return () => {
      fn();
      close();
    };
  }, [close]);

  const onToggleVisibilityClick = useCallback(async (visible: boolean) => {
    await mutation.mutateAsync({
      color: tag.base.color,
      hidden: !visible,
      includeSummary: tag.includeSummary,
      parentId: tag.parent?.id ?? null,
      tagId: tag.id,
      title: tag.base.name,
    });
    close();
  }, [
    mutation,
    close,
    tag,
  ]);

  // const disableEditing = !isTagEditable(tag);
  const disableDelete = !onDeleteTag;

  return (
    <PopperMenu ref={ref}>
      {!tag.parent?.id ?
        <PopperMenuItem onClick={closeWrapper(onCreateTag)}>
          Add Subtag
        </PopperMenuItem>
        : null}
      <PopperMenuItem onClick={() => onToggleVisibilityClick(!!tag.hidden)}>
        {tag.hidden ? 'Unhide' : 'Hide'}
      </PopperMenuItem>
      <Tooltip title={disableDelete ? disabledTagDeleteText : null} placement='top'>
        <PopperMenuItem disabled={disableDelete} onClick={closeWrapper(onDeleteTag)}>
          Delete
        </PopperMenuItem>
      </Tooltip>
    </PopperMenu>
  );
});

const disabledTagDeleteText = 'Tags with children cannot be deleted';